import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import Templates from "@/pages/Templates";
import TemplateUpload from "@/pages/TemplateUpload";
import Contracts from "@/pages/Contracts";
import ContractContentEditor from "@/pages/ContractContentEditor";
import ContractEditor from "@/pages/ContractEditor";
import ContractVersions from "@/pages/ContractVersions";
import OrganizationSettings from "@/pages/OrganizationSettings";
import UserManagement from "@/pages/UserManagement";
import AIChat from "@/pages/AIChat";
import AIAnalysis from "@/pages/AIAnalysis";
import Documents from "@/pages/Documents";
import Clients from "@/pages/Clients";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Billing from "@/pages/Billing";
import CalendarPage from "@/pages/CalendarPage";
import SearchPage from "@/pages/SearchPage";
import Surveillance from "@/pages/Surveillance";
import Consultation from "@/pages/Consultation";

// Components
import { Toaster } from "@/components/ui/sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem('auth_token', token);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, loading, login, logout, checkAuth };
};

// Auth Callback Component - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await fetch(`${API_URL}/api/auth/google-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const data = await response.json();
            navigate('/dashboard', { state: { user: data.user }, replace: true });
          } else {
            navigate('/login', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="text-center">
        <div className="spinner w-8 h-8 mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authentification en cours...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          if (userData.must_change_password && location.pathname !== '/change-password') {
            navigate('/change-password', { replace: true });
            return;
          }
          setIsAuthenticated(true);
          setUser(userData);
        } else {
          setIsAuthenticated(false);
          navigate('/login', { replace: true });
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user, setUser });
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash - process synchronously before routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Templates user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/templates/upload" element={
        <ProtectedRoute>
          {({ user, setUser }) => <TemplateUpload user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/contracts" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Contracts user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/contracts/new/:templateId" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ContractEditor user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/contracts/content/:contractId" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ContractContentEditor user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/contracts/:contractId" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ContractEditor user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/contracts/:contractId/versions" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ContractVersions user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/organization" element={
        <ProtectedRoute>
          {({ user, setUser }) => <OrganizationSettings user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          {({ user, setUser }) => <UserManagement user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/ai/chat" element={
        <ProtectedRoute>
          {({ user, setUser }) => <AIChat user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/ai/analysis" element={
        <ProtectedRoute>
          {({ user, setUser }) => <AIAnalysis user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Documents user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Clients user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/cases" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Cases user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/cases/:caseId" element={
        <ProtectedRoute>
          {({ user, setUser }) => <CaseDetail user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Billing user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          {({ user, setUser }) => <CalendarPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/search" element={
        <ProtectedRoute>
          {({ user, setUser }) => <SearchPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/surveillance" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Surveillance user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/consultation" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Consultation user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
